import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, LayoutGrid, List, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, cleanParams } from "@/lib/api";
import { BHK_OPTIONS, PROPERTY_TYPES, PROP_STATUSES } from "@/lib/constants";
import { useAssignableOptions, useUserMap } from "@/hooks/useData";
import { EmptyState, NSelect, PageHeader, Spinner } from "@/components/common";
import { PropertyCard, PropertyRow } from "@/components/properties/PropertyCard";
import { cn } from "@/lib/utils";

const KEYS = ["listing_type", "property_type", "city", "sector", "price_min", "price_max", "bhk", "status", "assigned_to", "q"];

export default function PropertiesPage() {
  const [params, setParams] = useSearchParams();
  const side = params.get("side") || "inventory";
  const view = params.get("view") || "cards";
  const filters = useMemo(() => Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""])), [params]);
  const [q, setQ] = useState(filters.q);
  const [sector, setSector] = useState(filters.sector);
  const userMap = useUserMap();
  const assignOpts = useAssignableOptions();
  const { data: cities = [] } = useQuery({ queryKey: ["prop-cities"], queryFn: async () => (await api.get("/properties/cities")).data });

  const set = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p, { replace: true });
  };

  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(params);
      [["q", q], ["sector", sector]].forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
      if (p.toString() !== params.toString()) setParams(p, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sector]);

  const apiParams = cleanParams({ ...filters, side });
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["properties", apiParams],
    queryFn: async () => (await api.get("/properties", { params: apiParams })).data,
  });
  const hasFilters = KEYS.some((k) => filters[k]);
  const listingOpts = side === "inventory" ? ["Sale", "Rent"] : [];

  return (
    <>
      <PageHeader
        title="Properties"
        testid="properties-title"
        subtitle={`${items.length} ${side === "inventory" ? "in inventory" : "purchase requirements"}`}
        actions={<Button size="sm" asChild data-testid="properties-add-button"><Link to="/properties/new"><Plus className="mr-1.5 h-4 w-4" />Add property</Link></Button>}
      />
      <div className="mb-4 flex items-center gap-2">
        <div className="flex rounded-full border bg-card p-0.5">
          {[["inventory", "Inventory"], ["requirements", "Requirements"]].map(([k, l]) => (
            <button key={k} data-testid={`properties-tab-${k}`} onClick={() => { const p = new URLSearchParams(params); p.set("side", k); p.delete("listing_type"); setParams(p, { replace: true }); }}
              className={cn("h-8 rounded-full px-4 text-xs font-semibold transition-colors", side === k ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>{l}</button>
          ))}
        </div>
        <div className="ml-auto flex rounded-full border bg-card p-0.5">
          <button data-testid="properties-view-cards" onClick={() => set("view", "")} className={cn("flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold", view === "cards" && "bg-primary text-primary-foreground")}><LayoutGrid className="h-3.5 w-3.5" />Cards</button>
          <button data-testid="properties-view-list" onClick={() => set("view", "list")} className={cn("flex h-7 items-center gap-1 rounded-full px-3 text-xs font-semibold", view === "list" && "bg-primary text-primary-foreground")}><List className="h-3.5 w-3.5" />List</button>
        </div>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-6">
        <div className="relative col-span-2">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <input data-testid="properties-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project, sector, address…" className="h-10 w-full rounded-lg border border-input bg-card pl-9 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        {side === "inventory" && <NSelect testid="properties-listing-filter" value={filters.listing_type} onChange={(v) => set("listing_type", v)} placeholder="Sale & Rent" options={listingOpts} />}
        <NSelect testid="properties-type-filter" value={filters.property_type} onChange={(v) => set("property_type", v)} placeholder="All types" options={PROPERTY_TYPES} />
        <NSelect testid="properties-city-filter" value={filters.city} onChange={(v) => set("city", v)} placeholder="All cities" options={cities} />
        <input data-testid="properties-sector-filter" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="Sector" className="h-10 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:border-primary" />
        <input data-testid="properties-price-min-filter" type="number" min="0" value={filters.price_min} onChange={(e) => set("price_min", e.target.value)} placeholder="Min ₹" className="h-10 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:border-primary" />
        <input data-testid="properties-price-max-filter" type="number" min="0" value={filters.price_max} onChange={(e) => set("price_max", e.target.value)} placeholder="Max ₹" className="h-10 rounded-lg border border-input bg-card px-3 text-sm outline-none focus:border-primary" />
        <NSelect testid="properties-bhk-filter" value={filters.bhk} onChange={(v) => set("bhk", v)} placeholder="Any BHK" options={BHK_OPTIONS} />
        <NSelect testid="properties-status-filter" value={filters.status} onChange={(v) => set("status", v)} placeholder="All statuses" options={PROP_STATUSES} />
        <NSelect testid="properties-assigned-filter" value={filters.assigned_to} onChange={(v) => set("assigned_to", v)} placeholder="Anyone" options={assignOpts} />
      </div>
      {hasFilters && (
        <button data-testid="properties-clear-filters" onClick={() => { setQ(""); setSector(""); setParams({ side, ...(view === "list" ? { view } : {}) }, { replace: true }); }} className="-mt-3 mb-4 flex items-center gap-1 text-xs font-semibold text-primary">
          <X className="h-3.5 w-3.5" />Clear filters
        </button>
      )}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState icon={Building2} testid="properties-empty" title="No properties found" text={hasFilters ? "Try clearing some filters." : "Add your first listing."} />
      ) : view === "list" ? (
        <div className="space-y-2" data-testid="properties-list">{items.map((p) => <PropertyRow key={p.id} p={p} owner={userMap[p.assigned_to]} />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="properties-grid">{items.map((p) => <PropertyCard key={p.id} p={p} owner={userMap[p.assigned_to]} />)}</div>
      )}
    </>
  );
}
